import React, { useState, useEffect, useContext, useCallback } from "react";
import config from "lib/config";
import { GET } from "Common/api";
import { API_PROGRAM } from "lib/endpoints/programs";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { Stack } from "Components/Base/Feedback/Stack";
import { Skeleton } from "Components/Base/Feedback/Skeleton";
import ProgramContext, { ProgramSettings } from "./ProgramContext";
import LangContext from "../../langContext";
import Header from "./components/Header";
import Cards from "./components/Cards";
import Pie from "./components/Pie";
import * as CONSTANTS from "../../constants";
import { getProgramProgress, filterProgramContent } from "../../functions";
import { StyledFilterButtons, StyledProgram } from "./style";

const Program = (props) => {
  const { managerView, personalId, isPreview } = props;
  const PERSONAL_ID = personalId || config.personalId;
  const PROGRAM_ID = props.activityId;
  const [program, setProgram] = useState(null);
  const [preview, setPreview] = useState(false);
  const { compact } = props;

  ProgramSettings.managerView = managerView;
  ProgramSettings.userId = PERSONAL_ID;

  const fetchData = useCallback(async () => {
    const fetchedProgramData =
      (await GET(API_PROGRAM, {
        programId: PROGRAM_ID,
        personalId: PERSONAL_ID,
      })) || [];
    const programData = { activityId: PROGRAM_ID, ...fetchedProgramData };

    const responseData =
      props.program !== undefined ? props.program : programData;
    setPreview(programData.isActivityAdminOnly || (isPreview ?? false));
    setProgram(responseData);
  }, []);

  const refreshProgram = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (!program) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" width={210} height={60} />
      </Stack>
    );
  }

  return (
    <ProgramContext.Provider value={ProgramSettings}>
      <LangContext.Provider value={props.languageContext}>
        <ProgramView
          data={program}
          compact={compact}
          onRefresh={refreshProgram}
          programParticipant={PERSONAL_ID}
          isPreview={preview}
          isManager={managerView}
        />
      </LangContext.Provider>
    </ProgramContext.Provider>
  );
};

export default withLanguageContext(Program, "Programs");

const ProgramView = (props) => {
  const languageContext = useContext(LangContext);
  const [activeFilterSelection, setActiveFilterSelection] = useState(
    CONSTANTS.PROGRESS_ALL,
  );
  const { steps, activityId } = props.data;
  const { programParticipant, isPreview, isManager } = props;
  const [filteredContent, setFilteredContent] = useState(steps);
  const emptyContent = filteredContent.length === 0;

  const programProgressValue = getProgramProgress(props.data);

  const handleFilter = (filter) => {
    const selectedContent = filterProgramContent(steps, filter);
    setActiveFilterSelection(filter);
    setFilteredContent(selectedContent);
  };

  const handleActivityChange = useCallback(() => {
    if (props.onRefresh) {
      props.onRefresh();
    }
  }, [props.onRefresh]);

  const buttons = [
    { value: languageContext("all"), filter: CONSTANTS.PROGRESS_ALL },
    {
      value: languageContext("lefttodo"),
      filter: CONSTANTS.PROGRESS_TODO,
    },
    {
      value: languageContext("Finished2"),
      filter: CONSTANTS.PROGRESS_DONE,
    },
  ];

  useEffect(() => {
    handleFilter(activeFilterSelection);
  }, [steps]);

  return (
    <>
      <StyledProgram className="bgColor-6">
        <Header data={props.data} isPreview={isPreview} />
        {
          <div className="flex flex-space-b align-c">
            <StyledFilterButtons
              buttons={buttons}
              onSelectFilter={handleFilter}
            />
            <div className="header-progress" id="programProgressPie">
              <Pie progress={programProgressValue} />
            </div>
          </div>
        }
        <hr />
        <div>
          {emptyContent && (
            <EmptyContent>{languageContext("noactivecontent")}</EmptyContent>
          )}
          <Cards
            activityId={activityId}
            steps={filteredContent}
            onRefresh={handleActivityChange}
            programParticipant={programParticipant}
            isPreview={isPreview}
            isManager={isManager}
          />
        </div>
      </StyledProgram>
    </>
  );
};

const EmptyContent = (props) => {
  return <div className="flex padding-10 align-c">{props.children}</div>;
};
